import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useSharedTranslation } from "@/shared/contexts/TranslateContext";

import Typography from "../Typography/Typography";
import Checkbox from "../Checkbox/Checkbox";
import SearchField from "../SearchField/SearchField";
import Input from "../Input/Input";

import { flagPlaceholder, makeLangImage } from "../../utils/url/url";
import { getOptionKey } from "../../utils/select";
import { addQuotes } from "../../utils/string/string";
import {
  handleKeyDownNavigation,
  handleKeyDownSelectItem
} from "../../utils/keyboard-navigation/keyboard-navigation";
import { useWhatInputIsLastPointerMouse } from "@/shared/what-input/useWhatInput";

import styles from "./ViewLanguages.module.scss";

const ViewLanguages = ({
  title,
  isHideCheckbox,
  isDisabled,
  renderSlotRightSide,
  onClickItem,
  onContextMenu,
  disableLastItem,
  items,
  itemsHighlighted,
  itemsDisabled,
  itemsChecked,
  itemsDefault,
}) => {
  const { t } = useSharedTranslation();
  const [searchValue, setSearchValue] = useState("");
  const refListItems = useRef();
  const isLastPointerMouse = useWhatInputIsLastPointerMouse();

  const searchValueLowerCase = searchValue.toLowerCase();

  const isItemPassesFilter = (item) => {
    if (!searchValue) {
      return true;
    }

    return (
      item.id?.toLowerCase().includes(searchValueLowerCase)
      || item.label?.toLowerCase().includes(searchValueLowerCase)
      || getOptionKey(item)?.toLowerCase().includes(searchValueLowerCase)
    );
  };

  const isAtLeastOneItemChecked = !!itemsChecked.length;
  let isAtLeastOneItemShown = false;

  const renderItem = (item) => {
    const itemId = getOptionKey(item);

    const imageUrl = makeLangImage(itemId, true);
    const isChecked = itemsChecked.includes(itemId);
    const isDefault = itemsDefault?.includes(itemId);
    const isDisabled = itemsDisabled?.includes(itemId);
    const isHighlighted = itemsHighlighted?.includes(itemId);
    const isPassesFilter = isItemPassesFilter(item);

    if (isPassesFilter) {
      isAtLeastOneItemShown = true;
    }

    const handleChange = (e) => {
      onClickItem(e, item, {
        isDisabled,
        isChecked,
        isDefault,
        isHighlighted,
      });
    };

    const handleKeyDown = (e) => handleKeyDownSelectItem(e, handleChange);

    const disableLastChecked = disableLastItem && isChecked && itemsChecked.length === 1;

    const isDisabledFinal = isDisabled || disableLastChecked;

    return (
      <li
        key={itemId}

        tabIndex={isDisabledFinal ? undefined : 0} // to be able to perform tab navigation
        data-focus={isAtLeastOneItemChecked ? undefined : true}
        aria-checked={isChecked}

        className={classNames(styles["lang-sel_item"], {
          [styles["is-default"]]: isDefault,
          [styles["is-checked"]]: isChecked,
          [styles["is-highlighted"]]: isHighlighted,
          [styles["is-disabled"]]: isDisabledFinal,
          [styles["hidden"]]: !isPassesFilter,
        })}
        onClick={handleChange}
        onKeyDown={handleKeyDown}
        onContextMenu={(e) => {
          if (onContextMenu) {
            onContextMenu(e, item, {
              isDisabled,
              isChecked,
              isDefault,
              isHighlighted,
            });
          }
        }}
      >
        {!isHideCheckbox && (
          <Checkbox
            isFocusable={false}
            className={styles["checkbox"]}
            disabled={isDisabled}
            checked={isChecked}
            partCheck={isDefault}
            onChange={(value, e) => {
              e.stopPropagation();
              handleChange(value);
            }}
          />
        )}

        <img
          alt={item.code}
          role="presentation"
          loading="lazy"
          className={styles["lang-sel-img"]}
          src={imageUrl}
          onError={(e) => {
            if (e.target.src.indexOf("flags") > -1) {
              e.target.src = flagPlaceholder;
            }
          }}
        />
        <Typography
          className={styles["label"]}
          hoverable
          variant={Typography.variant.body4}
          color={Typography.color.inherit}
        >
          {t(item.label)}
          {renderSlotRightSide && (
            <span className={styles["lang-sel-count"]}>{renderSlotRightSide(item)}</span>
          )}
        </Typography>
      </li>
    );
  };

  const handleKeyDownListItems = (e) => {
    handleKeyDownNavigation(refListItems.current, e, true);
  };

  return (
    <div
      className={classNames(styles["root"], {
        [styles["is-disabled"]]: isDisabled,
      })}>
      <div className={classNames(styles["head"], {
        [styles["with-title"]]: !!title,
      })}>
        {title && <span className={styles["title"]}>{title}</span>}

        <SearchField
          isWithDebounce
          autoFocus={isLastPointerMouse}
          className={styles["wrap-search"]}
          inputClassName={styles["input"]}
          value={searchValue}
          size={Input.size.small}
          isIconSearchEnabled={false}
          isIconClearVisible={!!searchValue}
          isIconSearchVisible={!searchValue}
          onChange={setSearchValue}
          onInputKeyDown={handleKeyDownListItems}
          onClear={() => setSearchValue("")}
          onInputClick={(event) => event.stopPropagation()}
        />
      </div>

      <div ref={refListItems} className={styles["wrap-items"]} onKeyDown={handleKeyDownListItems}>
        {items.map(renderItem)}
        {(searchValue && !isAtLeastOneItemShown) ? (
          <Typography
            key="placeholder"
            align="center"
            variant="body3"
            color="text-inactive"
          >
            <span style={{ textTransform: "capitalize" }}>{t("resultsZeroCount")}</span>
            &nbsp;{addQuotes(searchValue)}
          </Typography>
        ) : null}
      </div>
    </div>
  );
};

ViewLanguages.propTypes = {
  isDisabled: PropTypes.bool,
  isHideCheckbox: PropTypes.bool,
  title: PropTypes.string,
  items: PropTypes.array.isRequired,
  itemsChecked: PropTypes.array.isRequired,
  itemsDisabled: PropTypes.array,
  itemsDefault: PropTypes.array,
  itemsHighlighted: PropTypes.array,
  onClickItem: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func,
  renderSlotRightSide: PropTypes.func,
  disableLastItem: PropTypes.bool,
};

export default ViewLanguages;
